import React, { FC, useMemo } from 'react';

import {
  BentoCardsContainer,
  BentoCardsContainerProps,
} from '@ldp-web/component-library/ui/organisms/bento-cards-container';
import { MAX_GRID_ROWS } from '@ldp-web/component-library/ui/molecules/bento-card';
import { useBreakpoint } from '@ldp-web/component-library/hooks/usebreakpoint';

import { GenericAnalytics, IComponentCommonProps } from '@ldp/common-types';
import {
  SDMAnalyticEvents,
  SDMSnowplowProductEvents,
  TAnalyticsBentoCardCTAContext,
  TAnalyticsBentoCardProductTileClickContext,
  TAnalyticsBentoCardProductTileClickHoistedPositionContext,
  TCTAContext,
  TSDMAnalyticsComponentContext,
  TSDMComponentAnalyticsObject,
  TSDMDSModel,
  TSDMSnowplowProductContext,
  convertPriceToNumber,
  getCommonComponentContext,
} from '@ldp/sdm-analytics-utils';
import { useAnalyticsContext, useFeatureFlags } from '@ldp/common-contexts';

import { WrapperComponent } from '../wrapper';
import { useRedirectLink } from '../../utils';
import { useSDMRouterContext } from '@ldp/sdm-contexts';

type BentoCard = BentoCardsContainerProps['bentoCards'][number] & {
  callToAction: BentoCard['cta'];
  analytics: Record<string, unknown>;
  contentId: string;
};

/** Bento Cards Container props */
export type IBentoCardsContainerComponentProps = Omit<BentoCardsContainerProps, 'bentoCards'> & {
  bentoCards: Omit<BentoCard, 'cta'>[];
  /** id of component */
  id?: string;
} & IComponentCommonProps;

/** Renders Bento Cards Container Component */
export const BentoCardsContainerComponent: FC<IBentoCardsContainerComponentProps> = ({
  styling,
  bentoCards,
  contentId: bentoCardContainerContentId,
  position,
  id,
}) => {
  const router = useSDMRouterContext();
  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { isMobile, isTablet } = useBreakpoint();

  const redirectToPage = useRedirectLink(router);

  const { EnableHeliosSnowplow: isSnowplowEnabled } = getFeatureFlags();

  const numMaxProductTiles = useMemo(() => {
    if (isMobile || isTablet) return MAX_GRID_ROWS * 2;
    return MAX_GRID_ROWS * 3;
  }, [isMobile, isTablet]);

  const trackFnWrapper = (bentoCardIndex: number) => (context?: unknown) => {
    const { event } = context as Pick<
      TAnalyticsBentoCardProductTileClickContext | TAnalyticsBentoCardCTAContext,
      'event'
    >;

    const { headline, analytics, contentId: bentoCardContentId } = bentoCards[bentoCardIndex];

    const engineValue = (analytics?.dsModel as TSDMDSModel)?.engine || 'helios';
    const genericAnalytics = analytics?.genericAnalytics as GenericAnalytics | undefined;
    const dsModelData = genericAnalytics?.ds_model ?? (analytics?.dsModel as TSDMDSModel[] | TSDMDSModel | undefined);

    const commonContext = getCommonComponentContext(
      isSnowplowEnabled,
      true,
      dsModelData as TSDMComponentAnalyticsObject['dsModel'],
      genericAnalytics
    );

    if (event === SDMSnowplowProductEvents.TILE_CLICK) {
      const { productContext } = context as TAnalyticsBentoCardProductTileClickContext;

      const eventTrackingContext: TAnalyticsBentoCardProductTileClickHoistedPositionContext = {
        event,
        component: [
          {
            engine: engineValue,
            id: bentoCardContainerContentId!,
            name: bentoCardContainerContentId!,
            type: 'bentoCardContainerComponent',
            number_of_children: bentoCards.length,
            position,
          },
          {
            engine: engineValue,
            id: bentoCardContentId!,
            name: headline,
            type: 'bentoCard',
            position: bentoCardIndex + 1,
            parent_id: bentoCardContainerContentId!,
            number_of_children: numMaxProductTiles,
          },
        ],
        productContext: {
          ...productContext,
          name: productContext?.title || '',
          prices: {
            price: {
              value: convertPriceToNumber(productContext?.prices?.price?.value) || null,
            },
            wasPrice: {
              value: convertPriceToNumber(productContext?.prices.wasPrice.value) || null,
            },
          },
        } as TSDMSnowplowProductContext, //TODO: fix name and productPosition from CL
        productPosition: productContext?.productPosition,
        ...commonContext,
      };

      trackEvent('heliosBentoCardProductClick', eventTrackingContext);
    } else if (event === SDMAnalyticEvents.UI_ENGAGEMENT) {
      // the context we are receiving only needs to have a single component to represent the click
      const { component } = context as Omit<TAnalyticsBentoCardCTAContext, 'component'> & {
        component: TSDMAnalyticsComponentContext & TCTAContext;
      };

      const { click_action, click_element, click_url } = component;

      const eventTrackingContext: TAnalyticsBentoCardCTAContext = {
        event,
        component: [
          {
            engine: engineValue,
            id: bentoCardContainerContentId!,
            name: bentoCardContainerContentId!,
            type: 'bentoCardContainerComponent',
            number_of_children: bentoCards.length,
            position,
          },
          {
            engine: engineValue,
            id: bentoCardContentId!,
            name: headline,
            type: 'bentoCard',
            position: bentoCardIndex + 1,
            number_of_children: numMaxProductTiles,
            parent_id: bentoCardContainerContentId!,
            click_action,
            click_element,
            cta_url: click_url,
          },
        ],
        ...commonContext,
      };

      trackEvent('heliosBentoCardContainerCTA', eventTrackingContext);
    }
  };

  return (
    <WrapperComponent styling={styling} id={id}>
      <BentoCardsContainer
        bentoCards={bentoCards.map((bentoCard: BentoCard) => ({
          ...bentoCard,
          cta: bentoCard.callToAction,
        }))}
        trackingFnWrapper={trackFnWrapper}
        clickHandler={redirectToPage}
      />
    </WrapperComponent>
  );
};

export default BentoCardsContainerComponent;
